'use client';

export interface AnalyticsAttributionContext {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  utmId?: string;
  gclid?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  ttclid?: string;
  ttp?: string;
  msclkid?: string;
  gadSource?: string;
  wbraid?: string;
  gbraid?: string;
}

export type AnalyticsAttributionParamKey =
  | 'utm_source'
  | 'utm_medium'
  | 'utm_campaign'
  | 'utm_content'
  | 'utm_term'
  | 'utm_id'
  | 'gclid'
  | 'fbclid'
  | 'fbc'
  | 'fbp'
  | 'ttclid'
  | 'ttp'
  | 'msclkid'
  | 'gad_source'
  | 'wbraid'
  | 'gbraid';

export type AnalyticsAttributionParams = Partial<Record<AnalyticsAttributionParamKey, string>>;

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface AnalyticsAttributionResolutionOptions {
  hasAdvertisingConsent: boolean | undefined;
  storage?: StorageLike;
  cookieString?: string;
  nowMs?: number;
  randomValue?: number;
  writeCookie?: (cookie: string) => void;
}

const STORAGE_KEY = 'ss_analytics_attribution';

const PARAM_TO_CONTEXT_KEY: Record<AnalyticsAttributionParamKey, keyof AnalyticsAttributionContext> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
  utm_id: 'utmId',
  gclid: 'gclid',
  fbclid: 'fbclid',
  fbc: 'fbc',
  fbp: 'fbp',
  ttclid: 'ttclid',
  ttp: 'ttp',
  msclkid: 'msclkid',
  gad_source: 'gadSource',
  wbraid: 'wbraid',
  gbraid: 'gbraid',
};

const ATTRIBUTION_PARAM_KEYS = Object.keys(PARAM_TO_CONTEXT_KEY) as AnalyticsAttributionParamKey[];
const ADVERTISING_CONTEXT_KEYS = [
  'gclid',
  'fbclid',
  'fbc',
  'fbp',
  'ttclid',
  'ttp',
  'msclkid',
  'gadSource',
  'wbraid',
  'gbraid',
] as const satisfies ReadonlyArray<keyof AnalyticsAttributionContext>;
const META_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

function cleanValue(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function readStoredAttribution(storage: StorageLike | undefined): AnalyticsAttributionContext {
  if (!storage) {
    return {};
  }
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const result: AnalyticsAttributionContext = {};

    for (const contextKey of Object.values(PARAM_TO_CONTEXT_KEY)) {
      const value = parsed[contextKey];
      if (typeof value === 'string' && value.trim().length > 0) {
        result[contextKey] = value.trim();
      }
    }
    return result;
  } catch {
    return {};
  }
}

function writeStoredAttribution(storage: StorageLike | undefined, attribution: AnalyticsAttributionContext): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Storage can be unavailable in private browsing or embedded contexts.
  }
}

function getDefaultStorage(): StorageLike | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function getDefaultCookieString(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  try {
    return document.cookie;
  } catch {
    // Restricted cookie access must not interrupt analytics or order creation.
    return undefined;
  }
}

function getDefaultCookieWriter(): ((cookie: string) => void) | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  return (cookie) => {
    try {
      document.cookie = cookie;
    } catch {
      // Cookies can be unavailable in restricted or embedded contexts.
    }
  };
}

function parseCookieValue(cookieString: string | undefined, cookieName: string): string | undefined {
  if (!cookieString) {
    return undefined;
  }
  const match = cookieString
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${cookieName}=`));
  if (!match) {
    return undefined;
  }
  try {
    return decodeURIComponent(match.slice(cookieName.length + 1));
  } catch {
    return match.slice(cookieName.length + 1);
  }
}

function readVendorCookieAttribution(cookieString: string | undefined): AnalyticsAttributionContext {
  const attribution: AnalyticsAttributionContext = {};
  const fbc = cleanValue(parseCookieValue(cookieString, '_fbc'));
  const fbp = cleanValue(parseCookieValue(cookieString, '_fbp'));
  const ttp = cleanValue(parseCookieValue(cookieString, '_ttp'));
  if (fbc) {
    attribution.fbc = fbc;
  }
  if (fbp) {
    attribution.fbp = fbp;
  }
  if (ttp) {
    attribution.ttp = ttp;
  }
  return attribution;
}

function buildFbcFromFbclid(fbclid: string | undefined, nowMs: number): string | undefined {
  const cleaned = cleanValue(fbclid);
  if (!cleaned) {
    return undefined;
  }
  return `fb.1.${Math.trunc(nowMs)}.${cleaned}`;
}

function findFbcForFbclid(fbclid: string | undefined, candidates: Array<string | undefined>): string | undefined {
  const cleanedFbclid = cleanValue(fbclid);
  if (!cleanedFbclid) {
    return undefined;
  }
  return candidates.find((candidate) => {
    const cleanedCandidate = cleanValue(candidate);
    return Boolean(cleanedCandidate?.match(/^fb\.\d+\.\d+\./) && cleanedCandidate.endsWith(`.${cleanedFbclid}`));
  });
}

function buildFbp(nowMs: number, randomValue: number): string {
  const boundedRandomValue = Number.isFinite(randomValue) ? Math.min(Math.max(randomValue, 0), 0.999999999) : 0.5;
  const randomIdentifier = Math.floor(boundedRandomValue * 1_000_000_000) + 1_000_000_000;
  return `fb.1.${Math.trunc(nowMs)}.${randomIdentifier}`;
}

function withoutAdvertisingAttribution(attribution: AnalyticsAttributionContext): AnalyticsAttributionContext {
  const result = { ...attribution };
  for (const key of ADVERTISING_CONTEXT_KEYS) {
    delete result[key];
  }
  return result;
}

function writeMetaCookie(
  writeCookie: ((cookie: string) => void) | undefined,
  name: '_fbp' | '_fbc',
  value: string,
): void {
  writeCookie?.(
    `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${META_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax; Secure`,
  );
}

export function mapAttributionParamsToAnalyticsContext(
  params: AnalyticsAttributionParams,
): AnalyticsAttributionContext {
  const result: AnalyticsAttributionContext = {};

  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const value = cleanValue(params[key]);
    if (!value) {
      continue;
    }
    result[PARAM_TO_CONTEXT_KEY[key]] = value;
  }

  return result;
}

export function parseAnalyticsAttributionFromUrl(url: string | undefined): AnalyticsAttributionContext {
  if (!url) {
    return {};
  }
  try {
    const parsed = new URL(url);
    const params: AnalyticsAttributionParams = {};
    for (const key of ATTRIBUTION_PARAM_KEYS) {
      const value = parsed.searchParams.get(key);
      if (value) {
        params[key] = value;
      }
    }
    return mapAttributionParamsToAnalyticsContext(params);
  } catch {
    return {};
  }
}

export function resolvePersistedAnalyticsAttribution(
  url: string | undefined,
  options: AnalyticsAttributionResolutionOptions,
): AnalyticsAttributionContext {
  const {
    hasAdvertisingConsent,
    storage = getDefaultStorage(),
    cookieString,
    nowMs = Date.now(),
    randomValue = Math.random(),
    writeCookie = getDefaultCookieWriter(),
  } = options;
  const stored = readStoredAttribution(storage);
  const fromUrl = parseAnalyticsAttributionFromUrl(url);

  if (hasAdvertisingConsent !== true) {
    const merged = {
      ...withoutAdvertisingAttribution(stored),
      ...withoutAdvertisingAttribution(fromUrl),
    };
    if (Object.keys(fromUrl).length > 0) {
      writeStoredAttribution(storage, merged);
    }
    return merged;
  }

  const fromCookies = readVendorCookieAttribution(cookieString ?? getDefaultCookieString());
  const merged = { ...stored, ...fromCookies, ...fromUrl };

  const resolvedFbp = fromUrl.fbp ?? fromCookies.fbp ?? stored.fbp ?? buildFbp(nowMs, randomValue);
  const resolvedFbc =
    fromUrl.fbc ??
    (fromUrl.fbclid
      ? (findFbcForFbclid(fromUrl.fbclid, [fromCookies.fbc, stored.fbc]) ?? buildFbcFromFbclid(fromUrl.fbclid, nowMs))
      : (fromCookies.fbc ?? stored.fbc));
  merged.fbp = resolvedFbp;
  if (resolvedFbc) {
    merged.fbc = resolvedFbc;
  }

  if (fromCookies.fbp !== resolvedFbp) {
    writeMetaCookie(writeCookie, '_fbp', resolvedFbp);
  }
  if (resolvedFbc && fromCookies.fbc !== resolvedFbc) {
    writeMetaCookie(writeCookie, '_fbc', resolvedFbc);
  }

  if (
    Object.keys(fromUrl).length > 0 ||
    Object.keys(fromCookies).some((key) => Boolean(fromCookies[key as keyof AnalyticsAttributionContext])) ||
    !stored.fbp ||
    (Boolean(resolvedFbc) && !stored.fbc)
  ) {
    writeStoredAttribution(storage, merged);
  }

  return merged;
}
